import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Tooltip, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DocumentLink from "./DocumentLink";
import { API_BASE_URL } from "../config";

const API = `${API_BASE_URL}/documents`;

// Dates arrive as ISO (a date for an expiry, a timestamp for an upload); shown in
// the reader's locale like every other date the app renders inline.
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

/**
 * What a superseded file was, in words: "CV uploaded 4 June 2026", "Medical
 * Protection Society indemnity certificate expired 1 April 2026", "RRMT
 * certificate expired 12 May 2025".
 *
 * Composed here rather than served by the backend because it has to cope with
 * two kinds of row: one stamped with the context it was filed under
 * (`archive_context` / `archive_expiry`), and one replaced before those columns
 * existed, which has only the date it was uploaded to fall back on.
 */
export const describeHistoricalDocument = (doc) => {
  const uploaded = fmt(doc.uploaded_at);
  const expiry = fmt(doc.archive_expiry);
  const context = (doc.archive_context || "").trim();
  const when = expiry ? `expired ${expiry}` : (uploaded ? `uploaded ${uploaded}` : null);
  const parts = (() => {
    switch (doc.category) {
      case "cv":
        // A CV has no expiry, so this is always its upload date.
        return ["CV", uploaded ? `uploaded ${uploaded}` : null];
      case "indemnity_certificate":
        return [context ? `${context} indemnity certificate` : "Indemnity certificate", when];
      case "qualification_certificate":
        return [context ? `${context} certificate` : "Qualification certificate", when];
      // Both sides of the licence card. The context is the licence number it
      // showed, which is what tells two of them apart.
      case "driving_licence_front":
        return ["Driving licence (front)", context, when];
      case "driving_licence_back":
        return ["Driving licence (back)", context, when];
      default:
        return ["Document", when];
    }
  })();
  return parts.filter(Boolean).join(" ");
};

/**
 * The files a personnel record used to hold — a CV or certificate that has since
 * been replaced. Kept rather than lost: replacing a document only moves the
 * pointer, and this lists what it moved off (see core/personnel_documents.py for
 * how they're identified).
 *
 * Props:
 *  - personnelId: the saved record's id
 *  - version:     bump to reload (the instantly-saved sections can add to this list)
 *  - canDelete:   Super Admin only — Personnel.deleteDocumentHistory
 */
export default function PersonnelDocumentHistory({ personnelId, version = 0, canDelete = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // the document awaiting confirmation
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!personnelId) return;
    setLoading(true);
    axios.get(`${API}/history`, { params: { personnel_id: personnelId } })
      .then(res => setDocs(res.data))
      .finally(() => setLoading(false));
  }, [personnelId]);

  useEffect(() => { load(); }, [load, version]);

  const remove = () => {
    setBusy(true);
    axios.delete(`${API}/${confirm.id}`)
      .then(() => { setConfirm(null); load(); })
      .finally(() => setBusy(false));
  };

  if (loading && docs.length === 0) {
    return <CircularProgress size={20} />;
  }
  if (docs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No superseded documents — everything uploaded for this person is still current.
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        Replaced by a later upload, kept for the record.
      </Typography>
      {docs.map(doc => (
        <Box key={doc.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">{describeHistoricalDocument(doc)}</Typography>
            <DocumentLink doc={doc} />
          </Box>
          {canDelete && (
            <Tooltip title="Delete permanently (Super Admin)">
              <IconButton size="small" color="error" onClick={() => setConfirm(doc)}
                          aria-label={`Delete ${doc.original_filename || "document"}`}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      <Dialog open={confirm !== null} onClose={() => (busy ? null : setConfirm(null))}>
        <DialogTitle>Delete this document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete{" "}
            {confirm?.original_filename ? `"${confirm.original_filename}"` : "this file"}
            {confirm ? ` (${describeHistoricalDocument(confirm)})` : ""} and remove it from
            the record's history. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={remove} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
